import { transformTaskFromApiPayload } from '@/lib/sdk/chatbot/api';
import type { ChatbotFormData, TaskApiPayload, TaskProperties, WorkflowLinks } from '@/lib/sdk/chatbot/types';
import type { CreateBotRequestPayload } from '@/lib/sdk/ai-chat/types';

/**
 * The stored-task shape of an exported task. The export is a save request (TaskItem:
 * type, data, actions), while the editor loads tasks the way the GET endpoint returns
 * them (TaskProperties: task_type, task_type_metadata, task_actions).
 */
export function taskPropertiesFromItem(item: TaskApiPayload): TaskProperties {
  return {
    id: item.id,
    name: item.name,
    description: item.description ?? '',
    task_type: item.type,
    task_type_metadata: item.data ?? '',
    task_actions: item.actions ?? [],
    total_repetitions: 0,
    days: 0,
    approval_config: item.approval_config,
    validation_config: item.validation_config,
    extraction_config: item.extraction_config,
    data_source_config: item.data_source_config,
    startnew_config: item.startnew_config,
    auto_fill_config: item.auto_fill_config,
    skip_config: item.skip_config,
    counter_config: item.counter_config,
    writeback_configs: item.writeback_configs,
    mutable: item.mutable,
    sub_tasks: item.sub_tasks?.map(taskPropertiesFromItem),
    soft_ttl_value: item.soft_ttl_value,
    soft_ttl_unit: item.soft_ttl_unit,
    document_links: item.document_links,
    mask_output: item.mask_output,
    tags: item.tags,
  };
}

/** Editor form values for an exported draft, keeping every task id */
export function formDataFromExport(request: CreateBotRequestPayload): ChatbotFormData {
  return {
    title: request.title,
    template: request.template,
    ttl: request.ttl || 24,
    task_lifecycle: request.task_lifecycle || 'daily',
    ttl_unit: request.ttl_unit || 'hours',
    tasks: request.tasks.map((item) => transformTaskFromApiPayload(taskPropertiesFromItem(item))),
    tags: request.tags ?? [],
  };
}

/** JSON with object keys sorted, so equal values compare equal however they were built */
function canonical(value: unknown): string {
  return JSON.stringify(value, (_key, nested: unknown) => {
    if (nested && typeof nested === 'object' && !Array.isArray(nested)) {
      return Object.fromEntries(Object.entries(nested as Record<string, unknown>).sort(([left], [right]) => left.localeCompare(right)));
    }
    return nested;
  });
}

/**
 * Ids of the tasks an exported edit adds or changes, compared with the saved workflow
 * as the editor would load both.
 */
export function changedTaskIds(saved: TaskProperties[], updated: TaskApiPayload[]): Set<string> {
  const before = new Map(saved.map((task) => [task.id, canonical(transformTaskFromApiPayload(task))]));
  const changed = new Set<string>();
  for (const item of updated) {
    if (before.get(item.id) !== canonical(transformTaskFromApiPayload(taskPropertiesFromItem(item)))) {
      changed.add(item.id);
    }
  }
  return changed;
}

/** Whether two sets of links differ */
export function linksDiffer(current: WorkflowLinks | undefined, next: WorkflowLinks | undefined): boolean {
  return canonical(current ?? {}) !== canonical(next ?? {});
}
